import { DashboardLoanTransactionDetailModelDto } from '@treasury/api/channel';
import { formatUsdCurrency } from '@treasury/utils';
import { format } from 'date-fns';
import { emptyAdditionalAccountDetails } from '../../company/company-account.entity';
import { DomainEntity } from '../../shared';

const defaultLoanAccountDetail: DashboardLoanTransactionDetailModelDto = {
    currentBalance: 0,
    originalLoanTerm: '',
    accruedInterest: 0,
    lastPaymentDate: '',
    nextPaymentDate: '',
    paymentFrequency: '',
    availableCredit: undefined,
    lineOfCreditLimit: undefined,
    ...emptyAdditionalAccountDetails,
};

export class LoanAccountDetail extends DomainEntity<DashboardLoanTransactionDetailModelDto> {
    constructor(public source = defaultLoanAccountDetail) {
        super(source);
    }

    public createDefault(): this {
        return new LoanAccountDetail(defaultLoanAccountDetail) as this;
    }

    public get originalLoanAmount() {
        return formatUsdCurrency(this.source.originalLoanAmount);
    }

    public get currentBalance() {
        return formatUsdCurrency(this.source.currentBalance);
    }

    public get originalLoanTerm() {
        return this.source.originalLoanTerm;
    }

    public get interestRate() {
        return new Intl.NumberFormat('default', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(this.source.interestRate);
    }

    public get accruedInterest() {
        return formatUsdCurrency(this.source.accruedInterest);
    }

    public get totalAmountDue() {
        return formatUsdCurrency(this.source.totalAmountDue);
    }

    public get lastPaymentDate() {
        return asFormattedDateOrEmptyString(this.source.lastPaymentDate);
    }

    public get nextPaymentDate() {
        return asFormattedDateOrEmptyString(this.source.nextPaymentDate);
    }

    public get paymentFrequency() {
        return this.source.paymentFrequency;
    }

    public get remainingPayments() {
        return this.source.remainingPayments;
    }

    public get availableCredit() {
        if (!this.source.availableCredit && this.source.availableCredit !== 0) {
            return undefined;
        }
        return formatUsdCurrency(this.source.availableCredit || 0);
    }

    public get lineOfCreditLimit() {
        if (!this.source.lineOfCreditLimit && this.source.lineOfCreditLimit !== 0) {
            return undefined;
        }
        return formatUsdCurrency(this.source.lineOfCreditLimit || 0);
    }

    public get originalLoanDate() {
        return asFormattedDateOrEmptyString(this.source.originalLoanDate);
    }

    public get originalBalance() {
        return formatUsdCurrency(this.source.originalBalance ?? 0);
    }

    public get maturityDate() {
        return asFormattedDateOrEmptyString(this.source.maturityDate);
    }

    public get currentPayoff() {
        return formatUsdCurrency(this.source.currentPayoff ?? 0);
    }

    public get payoffGoodThru() {
        return asFormattedDateOrEmptyString(this.source.payoffGoodThru);
    }

    public get interestPaidYtd() {
        return formatUsdCurrency(this.source.interestPaidYtd ?? 0);
    }

    public get paymentDueDate() {
        return asFormattedDateOrEmptyString(this.source.paymentDueDate);
    }

    public get paymentAmountDue() {
        return formatUsdCurrency(this.source.paymentAmountDue ?? 0);
    }

    public get pastAmountDue() {
        return formatUsdCurrency(this.source.pastAmountDue ?? 0);
    }

    public get paymentTotalAmount() {
        return formatUsdCurrency(this.source.paymentTotalAmount ?? 0);
    }

    public get billedPrincipal() {
        return formatUsdCurrency(this.source.billedPrincipal ?? 0);
    }

    public get billedInterest() {
        return formatUsdCurrency(this.source.billedInterest ?? 0);
    }

    public get billedEscrow() {
        return formatUsdCurrency(this.source.billedEscrow ?? 0);
    }

    public get billedLateCharge() {
        return formatUsdCurrency(this.source.billedLateCharge ?? 0);
    }

    public get billedOtherCharge() {
        return formatUsdCurrency(this.source.billedOtherCharge ?? 0);
    }
}

function asFormattedDateOrEmptyString(date: Date | string | undefined) {
    if (!date) {
        return '';
    }
    return format(date, 'MM/dd/yyyy');
}
